import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupervisorFinding } from '@avisoma/shared';
import { findingMutationErrorToast, findingMutationToast } from '../../lib/findingMutationToast';
import { createSingleFlight } from '../../lib/singleFlight';
import { invalidateSupervisorQueries } from '../../lib/supervisorQueries';
import backend from '../../services/backend';
import { useToast } from '../Toast';
import type { FindingRevertHandle } from '../supervisorFindingRevert';

/**
 * Revert from the entity view. The view has no project, so the call goes through the finding's
 * raising project (`originProjectId`). One revert at a time; the entity refetch shows the result.
 * Failed requests are toasted here (the backend call opts out of the interceptor's generic toast).
 */
export function useEntityFindingRevert(): FindingRevertHandle {
  const queryClient = useQueryClient();
  const toast = useToast();
  // A second click can land before `isPending` re-renders the buttons disabled.
  const [flight] = useState(createSingleFlight);
  const mutation = useMutation({
    mutationFn: async (f: SupervisorFinding) => (await backend.revertSupervisorFinding(f.originProjectId, f.id)).data,
    onSuccess: (finding) => {
      const t = findingMutationToast('revert', finding);
      toast.show(t.message, t.type);
    },
    onError: (error) => {
      const t = findingMutationErrorToast(error);
      toast.show(t.message, t.type);
    },
    // Awaited: `isPending` holds until the refetch lands. Every project's list, not only the raising
    // one: a shared entity peeked from another project shows that project's findings too.
    onSettled: async (_data, _error, f) => {
      await invalidateSupervisorQueries(queryClient, f.originProjectId, { allProjects: true });
      flight.settle();
    },
  });
  return {
    revert: (f) => {
      if (flight.start()) mutation.mutate(f);
    },
    busy: mutation.isPending,
  };
}
